import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../services/authentication/authContext';
import { TextField, Button, Typography, IconButton, Tooltip, Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import TableSkeleton from '../common/TableSkeleton';
import AddIcon from '@mui/icons-material/Add';
import { DataGrid } from '@mui/x-data-grid';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import PageHeader from '../ui/PageHeader';

import { taskServices } from '../../services/taskServices';
import '../../css/Button.css';
import '../../css/DataTable.css';
import '../../css/Dialog.css';
import ConfirmationDialog from '../common/ConfirmationDialog';
import AccessAlarmIcon from '@mui/icons-material/AccessAlarm';
import StartIcon from '@mui/icons-material/PlayCircleOutline';
import Paper from '@mui/material/Paper';
import StopIcon from '@mui/icons-material/Stop';
import {
  Dialog, DialogActions, DialogContent, DialogTitle,
} from '@mui/material';
import { jwtDecode } from 'jwt-decode';
import { fetchUserDetails } from '../redux/userDetailsSlice';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { useSnackbar } from '../../utils/useSnackbar';

const Tasks = () => {
  const [tasksData, setTasksData] = useState([]);
  const [activeTask, setActiveTask] = useState([]);
  const [openDescriptionDialog, setOpenDescriptionDialog] = useState(false);
  const [taskToStop, setTaskToStop] = useState(null);
  const [description, setDescription] = useState('');
  const [isStopping, setIsStopping] = useState(false);
  const dispatch = useDispatch();
  const { data: userData, loading1, error } = useSelector(state => state.userDetails);
  const [loading, setLoading] = useState(true);
  const [searchTask, setSearchTask] = useState('');
  const [debouncedSearchTask, setDebouncedSearchTask] = useState(searchTask);
  const [filterTask, setFilterTask] = useState('');
  const [sortModel, setSortModel] = useState([{ field: 'updatedAt', sort: 'desc' }]);
  const [searchActiveTask, setSearchActiveTask] = useState('');
  const [debouncedSearchActiveTask, setDebouncedSearchActiveTask] = useState(searchActiveTask);
  const [filterActiveTask, setFilterActiveTask] = useState('');
  const [activeSortModel, setActiveSortModel] = useState([{ field: 'updatedAt', sort: 'desc' }]);
  const [openDialog, setOpenDialog] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);
  const { accessToken } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const navigationUrl = "/home/task"
  const navigationTaskTimeTrackUrl = "/home/task-time-track"
  const [rowCount, setRowCount] = useState(0);
  const [roles, setRoles] = useState([]);
  const isManageTask = Array.isArray(roles) && roles.includes('MANAGE_TASKS');
  const isManageJob = Array.isArray(roles) && (roles.includes('SUPER_ADMIN') || roles.includes('ADMIN') || roles.includes('SUB_ADMIN') || roles.includes('MANAGE_JOBS'));
  const isAdmin = Array.isArray(roles) && (roles.includes('ADMIN') || roles.includes('SUPER_ADMIN'));
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const pageSizeOptions = [5, 10, 15];
  const [activePaginationModel, setActivePaginationModel] = useState({
    page: 0,
    pageSize: 5,
  });
  const [activeRowCount, setActiveRowCount] = useState(0);
  const { showSnackbar, SnackbarComponent } = useSnackbar();
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [activeStartTimes, setActiveStartTimes] = useState({});
  const [tick, setTick] = useState(0);
  const [buttonsState, setButtonsState] = useState({
    startDisabled: false
  });
  const isSearchFirstRender = useRef(true);
  const isActiveSearchFirstRender = useRef(true);
  const isSortFirstRender = useRef(true);
  const isActiveSortFirstRender = useRef(true);
  const theme = useTheme();
  const isTabletOrBelow = useMediaQuery(theme.breakpoints.down('lg'));
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    if (activeTask && userData?.id) {
      const isUserTaskRunning = activeTask?.find(task => task?.assignedBy?.id === userData.id);

      setButtonsState({
        startDisabled: isUserTaskRunning
      });
    }
  }, [activeTask, userData]);

  useEffect(() => {
    const interval = setInterval(() => setTick(prev => prev + 1), 1000);
    return () => clearInterval(interval);
  }, []);

  const liveDurationsMemo = useMemo(() => {
    if (!activeStartTimes) return {};

    const updated = {};
    Object.entries(activeStartTimes).forEach(([id, startTime]) => {
      const start = Number(startTime);
      updated[id] = Math.max(0, Math.floor((Date.now() - start) / 1000)); // duration in seconds
    });

    return updated;
  }, [activeStartTimes, tick]);


  const formatHMS = totalSeconds => {
    const days = Math.floor(totalSeconds / 86400);
    const remAfterDays = totalSeconds % 86400;
    const hours = Math.floor(remAfterDays / 3600);
    const minutes = Math.floor((remAfterDays % 3600) / 60);
    if (days > 0) {
      return `${days}d:${String(hours).padStart(2, '0')}h:${String(minutes).padStart(2, '0')}m`;
    } else {
      const secs = remAfterDays % 60;
      return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
    }
  };

  const parseDate = (dateStr) => {
    // Use moment to parse the date in the given format
    const parsedDate = moment(dateStr, "DD-MM-YYYY hh:mm:ss A");

    // Check if the parsed date is valid
    if (!parsedDate.isValid()) {
      console.error("Invalid date format:", dateStr);
      return new Date(NaN);
    }

    // Return the parsed date as a JavaScript Date object
    return parsedDate.toDate();
  };

  useEffect(() => {
    if (accessToken && !userData.length) {
      const decodedToken = jwtDecode(accessToken);
      const userEmail = decodedToken.sub;
      let extractedRoles = [];

      if (Array.isArray(decodedToken.roles)) {
        extractedRoles = decodedToken.roles;
      } else if (typeof decodedToken.roles === "string") {
        extractedRoles = decodedToken.roles.split(",").map(role => role.trim());
      }

      setRoles(extractedRoles);
      dispatch(fetchUserDetails(userEmail));
    }
  }, [accessToken, dispatch, userData]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const taskField = sortModel?.[0]?.field || 'updatedAt';
      const taskCol = columns.find(c => c.field === taskField);
      const tasksSortField = taskCol?.sortField || taskField;
      const tasksSortDir = sortModel?.[0]?.sort || 'desc';

      const activeTaskField = activeSortModel?.[0]?.field || 'updatedAt';
      const activeTaskCol = ActiveColumns.find(c => c.field === activeTaskField);
      const activeSortField = activeTaskCol?.sortField || activeTaskField;
      const activeSortDir = activeSortModel?.[0]?.sort || 'desc';

      const res = await taskServices.getTaskData(debouncedSearchTask, filterTask, paginationModel.page, paginationModel.pageSize, activePaginationModel.page, activePaginationModel.pageSize, debouncedSearchActiveTask, filterActiveTask, tasksSortField, tasksSortDir, activeSortField, activeSortDir);

      if (res.tasks) {
        setTasksData(res.tasks.content || []);
        setRowCount(res.tasks.totalElements || 0);
      }
      if (res.activeTask) {
        const active = res.activeTask.content || [];
        setActiveTask(active);
        setActiveRowCount(res.activeTask.totalElements || 0);

        const startMap = {};
        active.forEach((task) => {
          if (task.startTime) {
            const parsed = moment(task.startTime, "DD-MM-YYYY hh:mm:ss A", true);
            if (parsed.isValid()) {
              startMap[task.id] = parsed.valueOf();
            }
          }
        });
        setActiveStartTimes(startMap);
        setRowCount(res.tasks.totalElements);
      }
    } catch (error) {
      if (error.response && error.response.status === 404) {
        setTasksData([]);
        setRowCount(0);
      } else {
        console.error("Error fetching data", error);
      }
    } finally {
      setLoading(false);
    }
  });

  useEffect(() => {
    if (accessToken) {
      fetchData();
    }
  }, [accessToken, paginationModel, debouncedSearchTask, filterTask, snackbarMessage, activePaginationModel, debouncedSearchActiveTask, filterActiveTask, sortModel, activeSortModel]);

  useEffect(() => {
    const message = localStorage.getItem("successMessage");
    if (message) {
      showSnackbar(message, 'success');
      setSnackbarMessage(message);
      localStorage.removeItem("successMessage");
    }
  }, []);

  const handleAddTask = () => {
    if (accessToken) {
      navigate(navigationUrl);
    }
  };

  const handleEditTask = (task) => {
    if (accessToken) {
      navigate(navigationUrl, { state: { taskData: task } });
    }
  };

  const handleTaskTimeTrack = (task) => {
    if (accessToken) {
      navigate(navigationTaskTimeTrackUrl, { state: { taskData: task } });
    }
  };

  const handleDeleteTask = (taskId) => {
    setTaskToDelete(taskId);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setTaskToDelete(null);
  };

  const handlePaginationModelChange = (newPaginationModel) => {
    setPaginationModel(newPaginationModel);
  };

  const confirmDelete = () => {
    if (taskToDelete) {
      taskServices.deleteTaskData(taskToDelete)
        .then(() => {
          setTasksData((prevData) => prevData.filter(task => task.id !== taskToDelete));
          showSnackbar('Task deleted successfully!', 'success');
          setSnackbarMessage('Task deleted successfully!');
        })
        .catch((error) => {
          showSnackbar('Error deleting task data', 'error');
          setSnackbarMessage('Error deleting task data');
        });
    }
    handleCloseDialog();
  };

  useEffect(() => {
    if (isSearchFirstRender.current) {
      isSearchFirstRender.current = false;
      return;
    }
    const timer = setTimeout(() => {
      const value = searchTask.trim();

      if (value === "" || value.length > 2) {
        setDebouncedSearchTask(value);
        setPaginationModel(prev => (prev.page === 0 ? prev : { ...prev, page: 0 }));
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTask]);

  useEffect(() => {
    if (isActiveSearchFirstRender.current) {
      isActiveSearchFirstRender.current = false;
      return;
    }
    const timer = setTimeout(() => {
      const value = searchActiveTask.trim();

      if (value === "" || value.length > 2) {
        setDebouncedSearchActiveTask(value);
        setActivePaginationModel(prev => (prev.page === 0 ? prev : { ...prev, page: 0 }));
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchActiveTask]);

  // Columns for the task table
  const columns = useMemo(() => [
    {
      field: 'title', headerName: t('task.title'), sortField: 'title', flex: 1, minWidth: isMobile ? 150 : 0, renderCell: (params) => (
        <Tooltip title={params.value}><span>{params.value || '-'}</span></Tooltip>
      )
    },
    {
      field: 'jobName', headerName: 'Job', sortField: 'job.name', flex: 2, minWidth: isMobile ? 150 : 0, renderCell: (params) => (
        <Tooltip title={params.value}><span>{params.value || '-'}</span></Tooltip>
      )
    },
    {
      field: 'startTime', headerName: "Login Date Time", sortField: 'startTime', flex: 2, minWidth: isMobile ? 180 : 0, renderCell: (params) => {
        <Tooltip title={params.value}><span>{params.value || '-'}</span></Tooltip>
      }
    },
    {
      field: 'endTime', headerName: "Logout Date Time", sortField: 'endTime', flex: 2, minWidth: isMobile ? 180 : 0, renderCell: (params) => {
        <Tooltip title={params.value}><span>{params.value || '-'}</span></Tooltip>
      }
    },
    {
      field: 'taskOwner', headerName: 'Task Owner', sortField: 'assignedBy.firstName', flex: 2, minWidth: isMobile ? 150 : 0, renderCell: (params) => {
        const ownerFirstname = params.row.assignedUserFirstName;
        const ownerLastname = params.row.assignedUserLastName;
        return (
          <Tooltip title={ownerFirstname + ' ' + ownerLastname}><span>{ownerFirstname + ' ' + ownerLastname || '-'}</span></Tooltip>
        );
      }
    },
    {
      field: 'actions',
      headerName: t('task.actions'),
      flex: 2.2,
      minWidth: isMobile ? 180 : 0,
      pinned: 'right',
      renderCell: (params) => {
        const isTimeTracked = params.row.startTime && params.row.endTime;

        return (
          <div>
            <Tooltip title={buttonsState.startDisabled ? "Already another task is running" : "Start Task"}>
              <span>
                <IconButton
                  color="primary"
                  onClick={() => handleStartStopTime(params.row.id, "start")}
                  disabled={buttonsState.startDisabled || userData?.id !== params?.row?.assignedUserId}
                  className="icon-button"
                >
                  <StartIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>

            <IconButton
              color="primary"
              onClick={() => handleTaskTimeTrack(params.row)}
              className="icon-button"
            //disabled={isTimeTracked}
            >
              <AccessAlarmIcon fontSize="small" />
            </IconButton>
            <IconButton
              color="primary"
              onClick={() => handleEditTask(params.row)}
              className="icon-button"
            >
              <EditIcon fontSize="small" />
            </IconButton>
            {
              isManageJob && <IconButton
                color="secondary"
                onClick={() => handleDeleteTask(params.row.id)}
                className="icon-button"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            }

          </div>
        );
      },
    }
  ], [isMobile, t, buttonsState.startDisabled, userData?.id, isManageJob]);

  const ActiveColumns = useMemo(() => [
    {
      field: 'clientName',
      headerName: 'Client',
      flex: 1,
      minWidth: isMobile ? 150 : 0,
      sortField: 'job.client.name',
    },
    {
      field: 'title',
      headerName: 'Task Title',
      flex: 1,
      minWidth: isMobile ? 150 : 0,
      sortField: 'title',
      renderCell: (params) => (
        <Tooltip title={params.value}><span>{params.value || '-'}</span></Tooltip>
      )
    },
    {
      field: 'jobName',
      headerName: 'Job',
      flex: 1,
      minWidth: isMobile ? 150 : 0,
      sortField: 'job.name',
    },
    {
      field: 'jobType',
      headerName: 'Job Type',
      flex: 1,
      minWidth: isMobile ? 120 : 0,
      sortField: 'job.type',
    },
    {
      field: 'taskOwner',
      headerName: 'Task Owner',
      flex: 1,
      minWidth: isMobile ? 150 : 0,
      sortField: 'assignedBy.firstName',
      renderCell: (params) =>
        `${params.row?.assignedUserFirstName || ''} ${params.row?.assignedUserLastName || ''}`.trim() || '-',
    },
    {
      field: 'runningTime',
      headerName: 'Running Time',
      flex: 1,
      minWidth: isMobile ? 120 : 0,
      renderCell: (params) =>
        liveDurationsMemo[params.row.id] != null ? formatHMS(liveDurationsMemo[params.row.id]) : '00:00:00',
    },
    {
      field: 'action',
      headerName: 'Action',
      flex: 0.5,
      minWidth: isMobile ? 80 : 0,
      sortable: false,
      renderCell: (params) => {
        const canStop = isAdmin || userData?.id === params.row?.assignedUserId;

        return (
          <IconButton
            color="error"
            onClick={() => handleStartStopTime(params.row.id, 'end')}
            className="icon-button"
            disabled={!canStop}
          >
            <StopIcon fontSize="small" />
          </IconButton>
        );
      },
    }
  ], [isMobile, liveDurationsMemo, isAdmin, userData?.id]);

  const filteredDataMemo = useMemo(() => {
    if (!tasksData) return [];

    const filter = (task, search) => {
      const lowerSearch = search?.trim().toLowerCase() || '';
      return (
        (task?.title?.toLowerCase().includes(lowerSearch)) ||
        (task.jobName && task.jobName.toLowerCase().includes(lowerSearch)) ||
        (task.assignedUserFirstName && task.assignedUserFirstName.toLowerCase().includes(lowerSearch)) ||
        (task.assignedUserLastName && task.assignedUserLastName.toLowerCase().includes(lowerSearch)) ||
        `${task.assignedUserFirstName} ${task.assignedUserLastName}`.toLowerCase().includes(lowerSearch)
      );
    };

    // Apply both filters
    return tasksData
      .filter(task => filter(task, searchTask) && filter(task, filterTask))
      .map(task => ({
        ...task,
        jobName: task.jobName,
        id: task?.id,
        title: task?.title,
        taskOwner: task?.assignedUserFirstName
      }));
  }, [tasksData, searchTask, filterTask]);

  const filteredActiveTaskMemo = useMemo(() => {
    if (!activeTask) return [];

    const filter = (task, search) => {
      const lowerSearch = search?.trim().toLowerCase() || '';
      return (
        (task?.title?.toLowerCase().includes(lowerSearch)) ||
        (task?.jobName?.toLowerCase().includes(lowerSearch)) ||
        (task?.clientName?.toLowerCase().includes(lowerSearch)) ||
        (task?.jobType?.toLowerCase().includes(lowerSearch)) ||
        (task?.assignedUserFirstName?.toLowerCase().includes(lowerSearch)) ||
        (task?.assignedUserLastName?.toLowerCase().includes(lowerSearch)) ||
        `${task?.assignedUserFirstName} ${task?.assignedUserLastName}`.toLowerCase().includes(lowerSearch)
      );
    };

    return activeTask
      .filter(task => filter(task, searchActiveTask) && filter(task, filterActiveTask))
      .map(task => ({
        ...task,
        clientName: task?.clientName,
        jobName: task?.jobName,
        jobType: task?.jobType,
        id: task?.id,
        title: task?.title,
        taskOwner: task?.assignedUserFirstName
      }));
  }, [activeTask, searchActiveTask, filterActiveTask]);

  const taskColumnVisibilityModel = useMemo(() => {
    if (isMobile) {
      return {
        // startTime: false,
        // endTime: false,
        // taskOwner: false,
      };
    }

    if (isTabletOrBelow) {
      return {
        // endTime: false,
      };
    }

    return {};
  }, [isMobile, isTabletOrBelow]);

  const activeTaskColumnVisibilityModel = useMemo(() => {
    if (isMobile) {
      return {
        // clientName: false,
        // jobType: false,
      };
    }

    if (isTabletOrBelow) {
      return {
        // clientName: false,
      };
    }

    return {};
  }, [isMobile, isTabletOrBelow]);

  function createData(taskTitle, jobName, taskOwnerName) {
    return { taskTitle, jobName, taskOwnerName };
  }
  const rows = [
    createData(),
  ];

  const handleDescriptionChange = (e) => {
    setDescription(e.target.value);

    if (e.target.value.length > 0) {
      setIsStopping(false);
    }
  };

  const handleStartStopTime = (taskId, type) => {

    if (type === 'end') {
      setTaskToStop(taskId);
      setOpenDescriptionDialog(true);

    } else {
      if (type === 'stop') {
        setIsStopping(true);
      }

      const data = { taskId, type, description };

      if ((type === 'start') || (type === 'stop' && description.length > 0)) {
        setIsStopping(false);

        taskServices.postStartStopTask(data)
          .then((response) => {
            showSnackbar(`Task ${type} time updated successfully!`, 'success');
            setSnackbarMessage(`Task ${type} time updated successfully for ${taskId} task!`);
            if (type === 'start') {
              setButtonsState({
                startDisabled: true,
              });
            }
            if (type === 'stop') {
              setTaskToStop(null);
              setDescription('');
              setOpenDescriptionDialog(false);
              setButtonsState({
                startDisabled: false,
              });

            }
          }).catch((error) => {
            const errorMsg = error?.response?.data || "Something went wrong";
            showSnackbar(errorMsg, 'error');
            setSnackbarMessage(errorMsg);
            setIsStopping(false);
          });
      }
    }
  };

  return (
    <div className="data-table-container">
      <PageHeader title="Tasks" />
      <br />

      <Dialog
        open={openDescriptionDialog}
        onClose={() => setOpenDescriptionDialog(false)}
        slotProps={{ paper: { className: 'modern-dialog' } }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle className="modern-dialog-title">
          <span className="modern-dialog-title-text">Enter Description of Task</span>
          <IconButton
            aria-label="close"
            onClick={() => setOpenDescriptionDialog(false)}
            className="modern-dialog-close-btn"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent className="modern-dialog-content">
          <TextField
            fullWidth
            multiline
            rows={4}
            value={description}
            onChange={handleDescriptionChange}
            placeholder="Enter task description"
            variant="outlined"
            sx={{ mt: 1 }}
            required
            error={isStopping && description.length === 0}
            helperText={isStopping && description.length === 0 ? '*Description is required' : ''}
          />
        </DialogContent>
        <DialogActions className="modern-dialog-actions">
          <Button
            onClick={() => setOpenDescriptionDialog(false)}
            variant='outlined'
            color="inherit"
            sx={{ borderColor: '#e2e8f0', color: '#64748b' }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => handleStartStopTime(taskToStop, 'stop')}
            color="primary"
            variant='contained'
            disabled={isStopping && description.length === 0}
            disableElevation
          >
            Stop
          </Button>
        </DialogActions>
      </Dialog>


      {/* Running Task at Header */}
      {filteredActiveTaskMemo && filteredActiveTaskMemo.length >= 0 && (
        <>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              justifyContent: 'space-between',
              gap: 1,
              mb: '0.625rem',
            }}
          >
            <Typography variant="h6" sx={{ fontSize: '1.125rem', mb: 0 }}>
              Active Task
            </Typography>
            <Box className="search-container" sx={{ width: { xs: '100%', sm: 'auto' } }}>
              <input
                type="text"
                placeholder="Type to search..."
                value={searchActiveTask}
                onChange={(e) => setSearchActiveTask(e.target.value)}
                className="search-input"
              />
            </Box>
          </Box>

          <Paper sx={{ marginBottom: '1.875rem' }}>
            <div className="data-table">
              <div className="data-grid-container" style={Object.keys(activeTask).length <= 1 ? { height: '30vh' } : {}}>
                {loading ? (
                  <Box sx={{ height: '100%', overflow: 'hidden' }}>
                    <TableSkeleton rows={3} columns={6} />
                  </Box>
                ) : (
                  <DataGrid
                    loading={false}
                    rows={filteredActiveTaskMemo}
                    columns={ActiveColumns}
                    pagination
                    paginationMode="server"
                    filterMode="server"
                    onFilterModelChange={(newFilterModel) => {
                      const value = newFilterModel.items?.[0]?.value || '';
                      setFilterActiveTask(value);
                    }}
                    sortingMode="server"
                    onSortModelChange={(newModel) => {
                      if (isActiveSortFirstRender.current) {
                        isActiveSortFirstRender.current = false;
                        if (!newModel?.length) return;
                      }
                      if (!newModel?.length && activeSortModel.length > 0) {
                        const currentField = activeSortModel[0].field;
                        const isColumnDefined = ActiveColumns.some(c => c.field === currentField);
                        if (!isColumnDefined) {
                          return;
                        }
                      }
                      setActiveSortModel(newModel);
                      setActivePaginationModel(prev => ({ ...prev, page: 0 }));
                    }}
                    sortModel={activeSortModel}
                    paginationModel={activePaginationModel}
                    rowCount={activeRowCount}
                    onPaginationModelChange={setActivePaginationModel}
                    pageSizeOptions={pageSizeOptions}
                    disableRowSelectionOnClick
                    getRowId={(row) => row.id}
                    hideFooter={activeRowCount <= 1}
                    columnVisibilityModel={activeTaskColumnVisibilityModel}
                  />
                )}
              </div>
            </div>
          </Paper>
        </>

      )}

      {/* Snackbar */}
      {SnackbarComponent}

      <div className="search-and-button">
        <Button
          variant="contained"
          onClick={handleAddTask}
          className="add-button"
          startIcon={<AddIcon />}
        >
          {t('task.add')}
        </Button>

        {/* General Search Filter */}
        <div className="search-container">
          <input
            type="text"
            placeholder="Type to search..."
            value={searchTask}
            onChange={(e) => setSearchTask(e.target.value)}
            className="search-input"
          />
        </div>
      </div>

      <div className="data-table">
        <div className="data-grid-container">
          {loading ? (
            <Box sx={{ height: '100%', overflow: 'hidden' }}>
              <TableSkeleton rows={8} columns={6} />
            </Box>
          ) : (
            <DataGrid
              loading={false}
              rows={filteredDataMemo}
              columns={columns}
              pagination
              paginationMode="server"
              filterMode="server"
              onFilterModelChange={(newFilterModel) => {
                const value = newFilterModel.items?.[0]?.value || '';
                setFilterTask(value);
              }}
              sortingMode="server"
              onSortModelChange={(newModel) => {
                if (isSortFirstRender.current) {
                  isSortFirstRender.current = false;
                  if (!newModel?.length) return;
                }
                if (!newModel?.length && sortModel.length > 0) {
                  const currentField = sortModel[0].field;
                  const isColumnDefined = columns.some(c => c.field === currentField);
                  if (!isColumnDefined) {
                    return;
                  }
                }
                setSortModel(newModel);
                setPaginationModel(prev => ({ ...prev, page: 0 }));
              }}
              sortModel={sortModel}
              paginationModel={paginationModel}
              rowCount={rowCount}
              pageSizeOptions={pageSizeOptions}
              onPaginationModelChange={handlePaginationModelChange}
              disableSelectionOnClick
              columnVisibilityModel={taskColumnVisibilityModel}
            />
          )}
        </div>

        {/* Delete Confirmation Dialog */}
        <ConfirmationDialog
          open={openDialog}
          onClose={handleCloseDialog}
          onConfirm={confirmDelete}
          title="Delete Task"
          message="Are you sure you want to delete this task?"
          messageSecond="*Associated time entries will also be deleted."
          confirmLabel="Delete"
          confirmColor="error"
        />
      </div>
    </div>
  );
};

export default Tasks;